import { useRef, useEffect, useCallback } from "react";
import { useSpring, animated } from "@react-spring/web";
import { useTheme } from "next-themes";
import { nodeColor } from "./org360Constants";
import type { Org360Node, Org360Link } from "./org360Types";

const W = 168;
const H = 108;
const PAD = 10;

interface Transform {
  scale: number;
  offsetX: number;
  offsetY: number;
  minX: number;
  minY: number;
}

interface Org360MinimapProps {
  nodes: Org360Node[];
  links: Org360Link[];
  selectedNodeId: string | null;
  visible: boolean;
  onNavigate: (graphX: number, graphY: number) => void;
}

export function Org360Minimap({
  nodes,
  links,
  selectedNodeId,
  visible,
  onNavigate,
}: Org360MinimapProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const transformRef = useRef<Transform | null>(null);
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";

  // Spring entrance — fades + scales in after graph settles
  const spring = useSpring({
    opacity: visible ? 1 : 0,
    scale: visible ? 1 : 0.88,
    y: visible ? 0 : 8,
    config: { tension: 280, friction: 24 },
    delay: visible ? 300 : 0,
  });

  // Draw minimap on canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Only draw when the graph has settled (positions are final)
    if (!visible) return;

    const positioned = nodes.filter(
      (n) => n.x !== undefined && n.y !== undefined,
    );
    if (positioned.length === 0) return;

    // Compute world bounds
    const xs = positioned.map((n) => n.x!);
    const ys = positioned.map((n) => n.y!);
    const minX = Math.min(...xs);
    const maxX = Math.max(...xs);
    const minY = Math.min(...ys);
    const maxY = Math.max(...ys);
    const rangeX = maxX - minX || 1;
    const rangeY = maxY - minY || 1;

    const availW = W - PAD * 2;
    const availH = H - PAD * 2;
    const scale = Math.min(availW / rangeX, availH / rangeY);

    // Center the scaled graph within the canvas
    const scaledW = rangeX * scale;
    const scaledH = rangeY * scale;
    const offsetX = PAD + (availW - scaledW) / 2;
    const offsetY = PAD + (availH - scaledH) / 2;

    transformRef.current = { scale, offsetX, offsetY, minX, minY };

    const toX = (gx: number) => offsetX + (gx - minX) * scale;
    const toY = (gy: number) => offsetY + (gy - minY) * scale;

    // Clear
    ctx.clearRect(0, 0, W, H);

    // Background
    ctx.fillStyle = isDark ? "rgba(15,17,23,0.85)" : "rgba(255,255,255,0.85)";
    ctx.fillRect(0, 0, W, H);

    // Edges
    ctx.lineWidth = 0.6;
    for (const link of links) {
      const src = link.source as Org360Node;
      const tgt = link.target as Org360Node;
      if (src?.x === undefined || tgt?.x === undefined) continue;
      ctx.beginPath();
      ctx.strokeStyle = isDark ? "rgba(148,163,184,0.28)" : "rgba(100,116,139,0.30)";
      ctx.moveTo(toX(src.x), toY(src.y!));
      ctx.lineTo(toX(tgt.x), toY(tgt.y!));
      ctx.stroke();
    }

    // Nodes
    for (const node of positioned) {
      const cx = toX(node.x!);
      const cy = toY(node.y!);
      const color = nodeColor(node.type);
      const isSelected = node.id === selectedNodeId;

      if (isSelected) {
        // Halo
        ctx.beginPath();
        ctx.arc(cx, cy, 5, 0, Math.PI * 2);
        ctx.fillStyle = `${color}44`;
        ctx.fill();
      }

      ctx.beginPath();
      ctx.arc(cx, cy, isSelected ? 2.5 : 1.8, 0, Math.PI * 2);
      ctx.fillStyle = isSelected ? color : `${color}cc`;
      ctx.fill();
    }
  // `visible` is in deps because node positions (x/y) are set by the force
  // simulation in-place — the array reference never changes, so we need the
  // visible flip (triggered by onEngineStop) to kick off the first real draw.
  }, [nodes, links, selectedNodeId, isDark, visible]);

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      const t = transformRef.current;
      if (!t) return;
      const rect = canvasRef.current!.getBoundingClientRect();
      const px = e.clientX - rect.left;
      const py = e.clientY - rect.top;
      // Invert transform: canvas px → graph world coords
      const gx = (px - t.offsetX) / t.scale + t.minX;
      const gy = (py - t.offsetY) / t.scale + t.minY;
      onNavigate(gx, gy);
    },
    [onNavigate],
  );

  return (
    <animated.div
      className="absolute bottom-4 left-4 z-20 rounded-xl overflow-hidden
        border border-border shadow-[0_4px_16px_hsl(var(--foreground)/0.1)]"
      style={{
        opacity: spring.opacity,
        transform: spring.y.to(
          (y) => `translateY(${y}px) scale(${spring.scale.get()})`,
        ),
        transformOrigin: "bottom left",
      }}
    >
      <canvas
        ref={canvasRef}
        width={W}
        height={H}
        onClick={handleClick}
        className="block cursor-crosshair"
        title="Click to navigate"
      />
    </animated.div>
  );
}
